import { ImportArticles } from 'components/domain/addArticles/AddArticleDock/ImportArticles'
import { ActionButton } from 'components/facets/ActionButton'
import { useAppContext, useWindowDimensions } from 'lib/hooks'
import { useStockStore } from 'lib/hooks/useStockStore'
import React, { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Filters, InventoryArticle, STOCK_TYPES } from 'shared'
import { SetState } from 'types'
import { DockAction, InventoryPresenterProps } from '..'
import './filter-dock.css'
import { FilterPresenter } from './FilterPresenter'

export interface FilterDockProps {
  filters: Filters
  filtersKey: string
  setFiltersKey: SetState<string>
  setFilters: SetState<Filters>
  fetchedAt: string
  defaultFilters: Filters
  areFiltersTouched: boolean
  sealedActive: boolean
  openExportAllProductsCsvDialog: () => void
  loading: boolean
  onAddArticles?: (articles: InventoryArticle[]) => void
  openImportFileDialog: () => void
  refreshStockAction: InventoryPresenterProps<''>['refreshStockAction']
  hideExportButton?: boolean
  extraActions?: DockAction[]
  showAddCardsButton?: boolean
  showFiltersButton?: boolean
  isEmptyInventory?: boolean
  parentPage?: string
}

export const FilterDock = React.memo(function FilterDock({
  filters,
  filtersKey,
  setFiltersKey,
  setFilters,
  fetchedAt,
  defaultFilters,
  areFiltersTouched,
  loading,
  openImportFileDialog,
  openExportAllProductsCsvDialog,
  onAddArticles,
  refreshStockAction,
  extraActions,
  showAddCardsButton,
  showFiltersButton,
  isEmptyInventory,
  parentPage,
}: FilterDockProps): JSX.Element {
  const { t } = useTranslation()
  const { isMobile, isPhone } = useAppContext()
  const { width } = useWindowDimensions()

  const [activeView, setActiveView] = useState('listing')
  const activeStockType = useStockStore((state) => state.activeStockType)
  const [activeDockButton, setActiveDockButton] = useState(activeStockType)
  const setActiveStockType = useStockStore((state) => state.setActiveStockType)

  return (
    <div
      className="filter-dock-container"
      style={{ width: isMobile ? (isPhone ? '60vw' : width / 3) : 'auto' }}
    >
      <div className="filter-dock-action-buttons-external-container">
        <div className="filter-dock-action-buttons-container">
          {showAddCardsButton && (
            <div
              className={
                activeDockButton === STOCK_TYPES.SINGLES ? `action-button-container mx-1` : `mx-1`
              }
            >
              <ActionButton
                data-testid="add-cards-btn"
                onClick={() => {
                  setActiveStockType(STOCK_TYPES.SINGLES)
                  setActiveDockButton(STOCK_TYPES.SINGLES)
                  setActiveView('addArticles')
                }}
                text={t('stockPricing.actions.addArticles')}
                icon="fa-light fa-image"
                color={'primary'}
              />
            </div>
          )}
          {showAddCardsButton && (
            <div
              className={
                activeDockButton === STOCK_TYPES.SEALED ? `action-button-container mx-1` : `mx-1`
              }
            >
              <ActionButton
                data-testid="add-sealed-btn"
                onClick={() => {
                  setActiveStockType(STOCK_TYPES.SEALED)
                  setActiveDockButton(STOCK_TYPES.SEALED)
                }}
                text={t('stockPricing.actions.addAccessories')}
                icon="fa-light fa-dice"
                color="primary"
              />
            </div>
          )}
          {showFiltersButton && (
            <div
              className={
                activeView === 'filters' && activeDockButton === 'filters'
                  ? `action-button-container mx-1`
                  : `mx-1`
              }
            >
              <ActionButton
                data-testid="show-filters-btn"
                onClick={() => {
                  setActiveView('filters')
                  setActiveDockButton('filters')
                }}
                disabled={isEmptyInventory}
                text={t('filters.title')}
                icon="fa-light fa-filter"
              />
            </div>
          )}
          {refreshStockAction && (
            <ActionButton
              data-testid="get-stock-btn"
              onClick={refreshStockAction.fn}
              disabled={loading}
              text={refreshStockAction.actionLabel}
              icon="fa-light fa-arrows-rotate"
              tooltip={refreshStockAction.tooltip?.(new Date(fetchedAt).toLocaleString())}
            />
          )}
          {extraActions?.map((action) => (
            <div
              key={action.key}
              className={
                action.key.includes(activeStockType) ? `action-button-container mx-1` : `mx-1`
              }
            >
              <ActionButton
                data-testid={action.key}
                onClick={() => {
                  action.fn()
                }}
                text={action.actionLabel}
                icon={action.icon}
                tooltip={action.tooltip?.(fetchedAt)}
              />
            </div>
          ))}
        </div>
      </div>
      {!onAddArticles || activeView === 'filters' ? (
        <div className="w-100 mb-3">
          <FilterPresenter
            filters={filters}
            filtersKey={filtersKey}
            defaultFilters={defaultFilters}
            areFiltersTouched={areFiltersTouched}
            setFiltersKey={setFiltersKey}
            setFilters={setFilters}
            parentPage={parentPage}
          />
        </div>
      ) : (
        <div>
          <div className="w-100 mb-3">
            <div>
              <ImportArticles
                onAddArticles={onAddArticles}
                openCsvImport={openImportFileDialog}
                openCsvExport={openExportAllProductsCsvDialog}
                tabSelected={'listing'}
              />
            </div>
          </div>
        </div>
      )}
    </div>
  )
})
